import type { AnalyticsDimension, AnalyticsView, PropertyDefinition } from "@shared/schema";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";
import { JobAnalyticsTab } from "./job-analytics-tab";

interface AnalyticsViewEditorProps {
  jobOpeningId: string;
  propertyDefs: PropertyDefinition[];
  /** Existing view when editing; undefined when creating */
  view?: AnalyticsView;
  onDone: () => void;
  onCancel: () => void;
}

type ChartType = "bar" | "pie";

interface DraftConfig {
  name: string;
  chartType: ChartType;
  primaryDimension: AnalyticsDimension;
  secondaryDimension: AnalyticsDimension | null;
}

function dimensionToSelectValue(dim: AnalyticsDimension): string {
  return dim.type === "stages" ? "stages" : `property:${dim.propertyId}`;
}

function selectValueToDimension(value: string): AnalyticsDimension {
  if (value === "stages") return { type: "stages" };
  const propertyId = value.replace("property:", "");
  return { type: "property", propertyId };
}

function ChartSkeleton() {
  return (
    <div className="p-6 space-y-4 animate-pulse">
      <div className="h-4 w-40 bg-muted rounded" />
      <div className="space-y-2 mt-6">
        {[75, 45, 88, 55, 32, 68, 42, 60].map((width) => (
          <div key={width} className="flex items-center gap-3">
            <div className="h-3 w-24 bg-muted rounded flex-shrink-0" />
            <div className="h-7 bg-muted rounded" style={{ width: `${width}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AnalyticsViewEditor({
  jobOpeningId,
  propertyDefs,
  view,
  onDone,
  onCancel,
}: AnalyticsViewEditorProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const groupableProps = propertyDefs.filter(
    (d) => d.type === "select" || d.type === "multi_select",
  );

  const [draft, setDraft] = useState<DraftConfig>({
    name: view?.name ?? "Nova view",
    chartType: view?.chartType ?? "bar",
    primaryDimension: view?.primaryDimension ?? { type: "stages" },
    secondaryDimension: view?.secondaryDimension ?? null,
  });

  // Debounce the dimensions used for the live preview query
  const [debouncedPrimary, setDebouncedPrimary] = useState(draft.primaryDimension);
  const [debouncedSecondary, setDebouncedSecondary] = useState(draft.secondaryDimension);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPrimary(draft.primaryDimension);
      setDebouncedSecondary(draft.secondaryDimension);
    }, 350);
    return () => clearTimeout(timer);
  }, [draft.primaryDimension, draft.secondaryDimension]);

  // Live preview query — fetches data for current draft dimensions
  const { data: previewData, isFetching: isPreviewFetching } = useQuery({
    ...orpc.jobOpenings.previewAnalyticsData.queryOptions({
      input: {
        id: jobOpeningId,
        primaryDimension: debouncedPrimary,
        secondaryDimension: debouncedSecondary,
      },
    }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // Synthetic view for chart rendering (uses draft chartType + name for display)
  const previewView: AnalyticsView = {
    id: "preview",
    jobOpeningId,
    name: draft.name || "Preview",
    chartType: draft.chartType,
    primaryDimension: debouncedPrimary,
    secondaryDimension: debouncedSecondary,
    order: 0,
    createdAt: view?.createdAt ?? new Date(),
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!view) throw new Error("Cannot save without a view");
      await rpcClient.jobOpenings.updateAnalyticsView({
        id: jobOpeningId,
        viewId: view.id,
        ...draft,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.jobOpenings.listAnalyticsViews.queryOptions({ input: { id: jobOpeningId } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.jobOpenings.getAnalyticsData.queryOptions({
          input: { id: jobOpeningId, viewId: view!.id },
        }).queryKey,
      });
      onDone();
    },
    onError: () => {
      toast({ title: "Erro ao salvar view", variant: "destructive" });
    },
  });

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
        <span className="text-sm font-semibold">{view ? "Editar view" : "Nova view"}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !draft.name.trim()}
          >
            {saveMutation.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>

      {/* Body: preview left + config right */}
      <div className="flex flex-1 min-h-0">
        {/* Preview panel */}
        <div className="flex-1 bg-muted/30 border-r overflow-auto relative">
          <div className="px-5 py-3 border-b text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            Preview
            {isPreviewFetching && (
              <span className="inline-block h-3 w-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            )}
          </div>

          {!previewData ? (
            <ChartSkeleton />
          ) : (
            <div
              className={isPreviewFetching ? "opacity-60 transition-opacity" : "transition-opacity"}
            >
              <JobAnalyticsTab
                view={previewView}
                jobOpeningId={jobOpeningId}
                previewData={previewData}
              />
            </div>
          )}
        </div>

        {/* Config panel */}
        <div className="w-80 flex flex-col gap-5 p-5 bg-background overflow-y-auto shrink-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Configuração
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="view-name">Nome</Label>
            <Input
              id="view-name"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Tipo de gráfico</Label>
            <div className="grid grid-cols-2 gap-2">
              {(["bar", "pie"] as const).map((type) => (
                <button
                  type="button"
                  key={type}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      chartType: type,
                      // Clear secondary dimension when switching to pie (not applicable)
                      ...(type === "pie" ? { secondaryDimension: null } : {}),
                    }))
                  }
                  className={`py-2 px-3 rounded-md border text-sm font-medium transition-colors ${
                    draft.chartType === type
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {type === "bar" ? "Barras" : "Pizza"}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Dimensão principal</Label>
            <Select
              value={dimensionToSelectValue(draft.primaryDimension)}
              onValueChange={(v) =>
                setDraft((d) => ({ ...d, primaryDimension: selectValueToDimension(v) }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stages">Etapas do Pipeline</SelectItem>
                {groupableProps.map((p) => (
                  <SelectItem key={p.id} value={`property:${p.id}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {draft.chartType === "bar" && (
            <div className="space-y-1.5">
              <Label>Agrupar por</Label>
              <Select
                value={
                  draft.secondaryDimension
                    ? dimensionToSelectValue(draft.secondaryDimension)
                    : "none"
                }
                onValueChange={(v) =>
                  setDraft((d) => ({
                    ...d,
                    secondaryDimension: v === "none" ? null : selectValueToDimension(v),
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhum —</SelectItem>
                  <SelectItem value="stages">Etapas do Pipeline</SelectItem>
                  {groupableProps.map((p) => (
                    <SelectItem key={p.id} value={`property:${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
